import React, { useReducer, useEffect, useRef, useContext } from 'react';

import DragAndDropReducer, { DragAndDropActions, initialDragAndDropState } from  '@/components/reducers/DragAndDropReducer'

import { BuildPath } from  '@/helpers/Paths';
import { Truncate } from  '@/helpers/Text';
import { ToastMessageType } from '../global/ToastNotification';
import { LockerManagementFileUploadContext } from '@/context/LockerManagementFileUploadContext';

import  '@/css/draganddrop.scss';

interface DragAndDropProps {
    initialFiles: File[],
    setFiles: (file: File[]) => void,
    setFileErrors: (val: boolean) => void
    setToast: (messageType: ToastMessageType, message: JSX.Element) => void
    uploadValidation: {
        maxFileSize: number
        extensions: string[]
    }
}

const DragAndDrop = ({setToast, uploadValidation={maxFileSize: 0, extensions:[]}}:DragAndDropProps) => {
    const { files, setFiles, uploadFiles, setUploadFiles, setFileErrors } = useContext(LockerManagementFileUploadContext);
    
    const [data, dispatch] = useReducer(DragAndDropReducer, {...initialDragAndDropState, fileList: files})

    const {inDropZone, dropDepth} = data;
    const {extensions: allowedExtensions ,maxFileSize: maximumFileSize} = uploadValidation
    
    const fileUpload = useRef<HTMLInputElement>();
    
    useEffect(() => {
        // disable default drag and drop behaviour in case the users misses dragging a file
        const ondragover = window.ondragover;
        const ondrop = window.ondrop;

        window.ondragover = (e: DragEvent) => e.preventDefault();
        window.ondrop =  (e: DragEvent) => e.preventDefault();

         // restore default behaviour on component dismount
        return () => {
            window.ondragover = ondragover;
            window.ondrop =  ondrop;
        }
    }, [])

    useEffect(() => {
        setUploadFiles(validateFiles(files))
    },[files]);

    function validateFiles(fileList: File[]): any {
        return fileList.map((file:File) => {
            const errors: string[] = []
            // file extension isn't in list
            if (allowedExtensions.length && !allowedExtensions.includes(`${file.name.split('.')[file.name.split('.').length -1 ].toLocaleLowerCase()}`)) {
                errors.push('Unsupported File Extension')
            }
            // file exceeds 100MB
            if (maximumFileSize && file.size > maximumFileSize) {
                errors.push('File Exceeds Maximum Size')
            } //100Mb 

            return {
                fileName: file.name,
                file,
                errors
            }
        })
        // alphabeticise
        .sort((x,y) => x.fileName.localeCompare(y.fileName))
        // errors on top
        .sort((x, y) => {
            if (x.errors.length && y.errors.length) {
                return 0;
            }
            else if(x.errors.length && !y.errors.length) {
                return -1
            }
            else if(y.errors.length && !x.errors.length) {
                return 1
            }
        })
    }

    useEffect(()=> {
        setFileErrors(uploadFiles.some(file => file.errors.length));
    }, [uploadFiles])

    function preventFileOpen(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
    }

    function handleDragLeave(e: React.DragEvent) {
        preventFileOpen(e);
        dispatch({ type: 'SET_DROP_DEPTH', dropDepth: data.dropDepth - 1 });
        
        if (dropDepth <= 1) {
            dispatch({type: DragAndDropActions.RESET_DROPZONE});
        }
        
    }

    function handleDragEnter(e: React.DragEvent) {
        preventFileOpen(e);
        dispatch({ type: DragAndDropActions.SET_DROP_DEPTH, dropDepth: data.dropDepth + 1 });
    }

    function handleDragOver(e: React.DragEvent) {
        preventFileOpen(e);
        e.dataTransfer.dropEffect = 'copy';
        dispatch({ type: DragAndDropActions.SET_IN_DROPZONE, inDropZone: true });
    }

    function handleDrop(e: React.DragEvent) {
        preventFileOpen(e);
        if (e.dataTransfer.items) {
            addFilesToList(removeFolders(e.dataTransfer.items));
            dispatch({type: DragAndDropActions.RESET_DROPZONE});
        }
    }

    function removeFolders(list: DataTransferItemList) {
        const fileArray: File[]  = []
        Array.from(list).forEach(item => {
            if(item.webkitGetAsEntry().isFile) {
                fileArray.push(item.getAsFile())
            }
        })
        return fileArray;
    }

    function addFilesToList(newfiles: File[]) {
        const existingFiles = files.map(f => f.name);
        newfiles = newfiles.filter( f => !existingFiles.includes(f.name));

        if ((newfiles.length + existingFiles.length) > 100) {
            setToast(ToastMessageType.WARNING, <>You have exceeded the maximum number of file uploads. Please review your files before uploading.</>)
            return;
        }

       setFiles([...files, ...newfiles]);
    }

    function onFileSubmit(fileList: {[key:number]: File, length: number}) {
        addFilesToList( Array.from(fileList));
        fileList = {length: 0};
    }

    function removeFile(file: File) {
        setFiles(files.filter(f => f !== file));
    }

    return (
        <section className="drag-and-drop-container">
            <div
                aria-label={'Drag File to Upload'}
                className={inDropZone ? 'drag-drop-zone inside-drag-area' : 'drag-drop-zone'}
                onDrop={handleDrop}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <img 
                    className="drag-drop-image"
                    src={`${BuildPath()}/images/locker/drag_drop.svg`}
                    alt="drag and drop icon"
                />
                <p className="drag-drop-text">
                    {inDropZone
                    ? <>Drop File Here to Upload</>
                    :<>Drag File to Upload or
                        <label htmlFor={"file-upload-button"} className="drag-drop-link-btn" tabIndex={0}>&nbsp;Choose File</label>
                        <input
                            ref={fileUpload}
                            id="file-upload-button"
                            type="file"
                            className="hidden"
                            multiple={true}
                            onChange={e => onFileSubmit(e.target.files)}
                            accept={allowedExtensions ? allowedExtensions.map(ext => `.${ext}`).join(',') : ''}
                        />
                    </>}
                </p>
            </div>
            <div className="uploaded-files-list">
                <div className={`uploaded-files-list-head ${uploadFiles.length > 100 ? 'error': ''}`}>
                    <p>File Name</p>
                    <p className='file-count'> {uploadFiles.length} / 100</p>
                </div>
                <div className="uploaded-file-list-files narrow-scroll-bar">
                    <ul>
                        {!!uploadFiles.length && uploadFiles.map((f, idx) => (
                            <li key={`fileupload-${idx}`}>
                                <div className={'file-list-file-wrapper'}>
                                    <span className='name-label'>{Truncate(f.fileName, 30)}</span>
                                    {!!f.errors.length && 
                                        <span className={`micro-text ${!!f.errors.length && 'error'}`}>
                                            {f.errors[0]}
                                        </span>
                                    }
                                </div>
                                
                                <button
                                    className="file-upload-delete-btn"
                                    onClick={() => removeFile(f.file)}
                                    aria-label={`delete ${f.fileName}`}
                                > 
                                    <img 
                                        className="delete-button-img"
                                        src={`${BuildPath()}/images/locker/icons/delete_button_icon.svg`}
                                        alt={""}
                                    />
                                </button>
                            </li>
                        ))} 
                    </ul>
                    {!files.length && 
                    <div className="not-found-wrapper">
                        <img src={`${BuildPath()}/images/locker/filenotFound.svg`} alt="batch icon"/>
                        <h4>No Files are uploaded</h4>
                    </div>
                    }
                </div>
            </div>
            
    
        </section>
    )
        
}

export default DragAndDrop;